// Helper functions for handling date ranges and time grains

export const getTimeGrain = (interval: string) => {
  
  switch (interval?.toLowerCase()) {
    case 'daily':
    case '1 day':
    case 'yesterday': 
      return 'P1D';
    case 'weekly':
    case '1 week':
    case '1W':
      return 'P1W';
    case 'monthly':
    case '1 month':
    case '1m':
      return 'P1M';
    case '3m':
    case '3 months':
      return 'P3M';
    case 'yearly':
    case '1 year':
    case '1y':
      return 'P1Y';
    case 'custom':
      return 'P1D';
    default:
      return 'P1M';  // Default to monthly
  }
};

export const getTimeRangeForInterval = (selectedInterval: string) => {
  const currentDate = new Date();
  let startDate = new Date();
  let endDate = new Date();

  switch (selectedInterval?.toLowerCase()) {
    case 'yesterday':
      // Yesterday (previous complete day)
      startDate = new Date(currentDate);
      startDate.setDate(currentDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'daily':
    case '1 day':
    case '1d':
      // Last 24 hours
      startDate = new Date(currentDate.getTime() - (24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(currentDate);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'weekly':
    case '1 week':
    case '1w':
      // Last 7 days
      startDate = new Date(currentDate.getTime() - (7 * 24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(currentDate);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'monthly':
    case '1 month':
    case '1m':
      // Previous complete month
      startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case '3m':
    case '3 months':
      // Previous 3 complete months
      startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'yearly':
    case '1 year':
    case '1y':
      // Previous complete year
      startDate = new Date(currentDate.getFullYear() - 1, 0, 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(currentDate.getFullYear() - 1, 11, 31);
      endDate.setHours(23, 59, 59, 999);
      break;
    default:
      // Default to current month
      startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
  }

  return {
    currentMonthStartDate: startDate.getTime(),
    currentMonthEndDate: endDate.getTime()
  };
};

export const calculateDaysBetweenDates = (startDate: number, endDate: number): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const formatRangeForSuperset = (range: { currentMonthStartDate: number; currentMonthEndDate: number }) => {
  // Normalize using LOCAL time to avoid UTC shifting the date backward
  const toLocalIso = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const start = new Date(range.currentMonthStartDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(range.currentMonthEndDate);
  end.setHours(23, 59, 59, 999);

  return `${toLocalIso(start)} : ${toLocalIso(end)}`;
};

export const formatDateTimeForSuperset = (timestamp: number) => {
  return new Date(timestamp).toISOString().slice(0, 19);
};

export const formatDateByInterval = (date: Date, interval: string) => {
  if (isNaN(date.getTime())) return '';

  switch (interval?.toLowerCase()) {
    case '1 day':
    case '1d':
    case 'yesterday':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric'
      });
    case '1 week':
    case '1w':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    case '1 month':
    case '1m':
    case '3m':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    case '1 year':
    case '1y':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric'
      });
    default:
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
  }
};

export const getDynamicDateRange = (selectedInterval: string) => {
  const now = new Date();
  let startDate = new Date();
  let endDate = new Date(now);

  // Set end date to end of current day
  endDate.setHours(23, 59, 59, 999);

  switch (selectedInterval?.toLowerCase()) {
    case 'daily':
    case '1 day':
    case '1d':
      // Last 24 hours
      startDate = new Date(now.getTime() - (24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
      break;
      case 'yesterday':
      // Yesterday (previous complete day)
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'weekly':
    case '1 week':
    case '1w':
      // Last 7 days
      startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'monthly':
    case '1 month':
    case '1m':
      // Start from first day of previous month
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'yearly':
    case '1 year':
    case '1y':
      // Start from first day of previous year
      startDate = new Date(now.getFullYear() - 1, 0, 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now.getFullYear() - 1, 11, 31);
      endDate.setHours(23, 59, 59, 999);
      break;
      case '3m':
        case '3 months':
          // Previous 3 complete months
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          endDate.setHours(23, 59, 59, 999);
          break;
    default:
      // Default to current month
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
  }

  return {
    startDate: startDate.toISOString().slice(0, 19),
    endDate: endDate.toISOString().slice(0, 19)
  };
};

export const getTrendLineDateRange = (interval: string) => {
  const now = new Date();
  let startDate = new Date();
  let endDate = new Date(now);

  // Always set end date to end of current day
  endDate.setHours(23, 59, 59, 999);

  switch (interval?.toLowerCase()) {
    case 'daily':
    case '1 day':
    case '1d':
      // Last 24 hours
      startDate = new Date(now.getTime() - (24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
      break;
      case 'yesterday':
      // Yesterday (previous complete day)
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'weekly':
    case '1 week':
    case '1w':
      // Last 7 days
      startDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'monthly':
    case '1 month':
    case '1m':
      // Last 30 days
      startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
      break;
    case '3m':
    case '3 months':
      // Previous 3 complete months
      startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'yearly':
    case '1 year':
    case '1y':
      // Last 365 days
      startDate = new Date(now.getTime() - (365 * 24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
      break;
    default:
      // Default to last 30 days
      startDate = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      startDate.setHours(0, 0, 0, 0);
  }

  return {
    currentMonthStartDate: startDate.getTime(),
    currentMonthEndDate: endDate.getTime()
  };
};



// Helper function to format the date range for trend line graph payload
export const formatTrendLineRange = (range: { currentMonthStartDate: number; currentMonthEndDate: number }) => {
  const start = new Date(range.currentMonthStartDate);
  const end = new Date(range.currentMonthEndDate);
  
  // Ensure proper timezone handling
  start.setUTCHours(0, 0, 0, 0);
  end.setUTCHours(23, 59, 59, 999);
  
  return {
    startDate: start.toISOString().slice(0, 19),
    endDate: end.toISOString().slice(0, 19)
  };
};

export interface CustomerHealthTrendTimeConfig {
  timeGrain: string;
  startDate: Date;
  endDate: Date;
  dateFormat: Intl.DateTimeFormatOptions;
}

export const getCustomerHealthTrendTimeConfig = (interval: string): CustomerHealthTrendTimeConfig => {
  const now = new Date();
  let startDate = new Date();
  let endDate = new Date(now);
  let timeGrain = 'P1M'; // Default time grain
  let dateFormat: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric'
  };

  // Set end date to end of current day
  endDate.setHours(23, 59, 59, 999);

  switch (interval?.toLowerCase()) {
    case 'daily':
    case '1 day':
      // Last 24 hours with hourly grain
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      timeGrain = 'PT1H'; // Hourly grain
      dateFormat = {
        hour: 'numeric',
        hour12: true
      };
      break;

    case 'weekly':
    case '1 week':
      // Last 7 days with daily grain
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      timeGrain = 'P1D'; // Daily grain
      dateFormat = {
        month: 'short',
        day: 'numeric'
      };
      break;

    case 'monthly':
    case '1 month':
      // Current month with weekly grain
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
      timeGrain = 'P1W'; // Daily grain
      dateFormat = {
        month: 'short',
        day: 'numeric'
      };
      break;

    case 'yearly':
    case '1 year':
      // Current year with monthly grain
      startDate = new Date(now.getFullYear(), 0, 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), 11, 31);
      endDate.setHours(23, 59, 59, 999);
      timeGrain = 'P1M'; // Monthly grain
      dateFormat = {
        month: 'short'
      };
      break;

    default:
      // Default to current month with monthly grain
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
      timeGrain = 'P1W';
      dateFormat = {
        month: 'short',
        day: 'numeric'
      };
  }

  return {
    timeGrain,
    startDate,
    endDate,
    dateFormat
  };
};

export const getComparisonText = (interval: string) => {
    switch (interval?.toLowerCase()) {
        case 'yearly':
        case '1 year':
          case '1Y':
            return 'vs last year';
        case 'monthly':
        case '1 month':
          case '1m':
            return 'vs last month';
            case '3 month':
          case '3m':
            return 'vs last 3 months';
        case 'weekly':
        case '1 week':
          case '1w':
            return 'vs last week';
        case 'daily':
        case '1 day':
          case '1d':
            case 'today':
            return 'vs last day';
          case 'yesterday':
              return 'vs yesterday';    
        case 'custom':
            return 'vs last period';
        default:
            return 'vs last month';
    }
};

export const getCustomTimeGrainForTrendLine = (range: { currentMonthStartDate: number; currentMonthEndDate: number }) => {
    const daysDiff = calculateDaysBetweenDates(range.currentMonthStartDate, range.currentMonthEndDate);
    
    if (daysDiff <= 7) {
        return 'PT1H'; // Hourly data
    } else if (daysDiff <= 31) {
        return 'P1D';  // Daily data
    } else if (daysDiff <= 365) {
        return 'P1W';  // Weekly data
    } else {
        return 'P1M';  // Monthly data
    }
};

export const getTimeGrainForTrendLine = (interval: string, range: { currentMonthStartDate: number; currentMonthEndDate: number }) => {
    switch (interval?.toLowerCase()) {
        case 'yearly':
        case '1 year':
            return 'P1M';
        case 'monthly':
        case '1 month':
            return 'P1W';
        case 'daily':
        case '1 day':
            return 'PT1H';
        case 'weekly':
        case '1 week':
            return 'P1D';
        case 'custom':
            return getCustomTimeGrainForTrendLine(range);
        default:
            return 'P1M';
    }
};

export const getTrendLineTimeGrain = (interval: string) => {
  switch (interval?.toLowerCase()) {
    case 'yearly':
    case '1 year':
      return 'P1M';  // Monthly grain for yearly view
    case 'monthly':
    case '1 month':
      return 'P1W';  // Weekly grain for monthly view
    case 'weekly':
    case '1 week':
      return 'P1D';  // Daily grain for weekly view
    case 'daily':
    case '1 day':
      return 'PT1H'; // Hourly grain for daily view
    default:
      return 'P1W';  // Default to weekly grain
  }
};

const getWeekNumber = (date: Date): number => {
  const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  return Math.ceil((date.getDate() + firstDayOfMonth.getDay()) / 7);
};

export const formatTrendLineDate = (date: Date, interval: string): string => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  switch (interval?.toLowerCase()) {
    case 'yearly':
    case '1 year':
      return `${monthNames[date.getMonth()]} ${date.getFullYear().toString().slice(2)}`;
    case 'monthly':
    case '1 month': {
      const weekNum = getWeekNumber(date);
      return `${monthNames[date.getMonth()]} Week ${weekNum}`;
    }
    case 'weekly':
    case '1 week':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'daily':
    case '1 day':
      return date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
    default:
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

export const getTrendLineDateFormat = (interval: string): Intl.DateTimeFormatOptions => {
  switch (interval?.toLowerCase()) {
    case 'yearly':
    case '1 year':
      return { month: 'short' };
    case 'monthly':
    case '1 month':
      return { month: 'short', day: 'numeric' };
    case 'weekly':
    case '1 week':
      return { month: 'short', day: 'numeric' };
    case 'daily':
    case '1 day':
      return { hour: 'numeric', hour12: true };
    default:
      return { month: 'short', day: 'numeric' };
  }
};

export const getTrendLineDrillDownConfig = (interval: string): { timeRange: any, timeGrain: string } => {
  const now = new Date();
  let startDate = new Date();
  let endDate = new Date(now);
  let timeGrain = 'P1M'; // Default time grain

  switch (interval?.toLowerCase()) {
    case 'yearly':
      // Start from current year and go back 11 years
      endDate = new Date(now.getFullYear(), 11, 31); // End of current year
      startDate = new Date(now.getFullYear() - 11, 0, 1); // Start of year 11 years ago
      timeGrain = 'P1Y';
      break;

    case 'monthly':
      // Start from current month and go back 11 months
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // End of current month
      startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1); // Start of month 11 months ago
      timeGrain = 'P1M';
      break;

    case 'weekly':
      // Start from current week and go back 11 weeks
      endDate = new Date(now); // Current date
      startDate = new Date(now.getTime() - (11 * 7 * 24 * 60 * 60 * 1000)); // 11 weeks ago
      timeGrain = 'P1W';
      break;

    case 'daily':
      // Start from current day and go back 11 days
      endDate = new Date(now); // Current date
      startDate = new Date(now.getTime() - (11 * 24 * 60 * 60 * 1000)); // 11 days ago
      timeGrain = 'P1D';
      break;

    case 'present day':
      // Start from current hour and go back 11 hours
      endDate = new Date(now); // Current time
      startDate = new Date(now.getTime() - (11 * 60 * 60 * 1000)); // 11 hours ago
      timeGrain = 'PT1H';
      break;

    default:
      // Default to current month and back 11 months
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // End of current month
      startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1); // Start of month 11 months ago
      timeGrain = 'P1M';
  }

  // Set proper time boundaries
  if (interval?.toLowerCase() === 'present day') {
    // For hourly view, keep the exact hours
    endDate.setMinutes(59, 59, 999);
    startDate.setMinutes(0, 0, 0);
  } else {
    // For all other views, set to start/end of day
    endDate.setHours(23, 59, 59, 999);
    startDate.setHours(0, 0, 0, 0);
  }

  return {
    timeRange: {
      currentMonthStartDate: startDate.getTime(),
      currentMonthEndDate: endDate.getTime()
    },
    timeGrain
  };
};

export const formatTrendLineDrillDownDate = (date: Date, interval: string): string => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  switch (interval?.toLowerCase()) {
    case 'yearly':
      // Just show the year for yearly view
      return date.getFullYear().toString();
    case 'monthly':
      return `${monthNames[date.getMonth()]} ${date.getFullYear().toString().slice(2)}`;
    case 'weekly': {
      const weekNum = getWeekNumber(date);
      return `${monthNames[date.getMonth()]} Week ${weekNum}`;
    }
    case 'daily': {
      // Format: DD-MM-YYYY
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
    case 'present day': {
      // Format: HH.00Hr (24-hour format)
      const hours = date.getHours().toString().padStart(2, '0');
      return `${hours}.00Hr`;
    }
    default:
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}; 